import * as Logger from './logger.js'
import { Auth } from './auth.js'

class UserSessionSingleton {
    state = {};
    initialized = false;

    constructor() {
        this.state['timeScope'] = 'month';
        //this.state['timeScope'] = 'all';
    }

    async init() {
        this.set("host", new URL(window.location.href).hostname);
        this.set("clouds", []);
        this.set("filtered_clouds", []);
        this.set("selected_clouds", []);
        this.set("providers", []);
        this.set("filtered_providers", []);
        this.set("user_providers", []);
        this.set("selected_providers", []);
        this.set("orgs", []);
        this.set("filtered_orgs", []);
        this.set("selected_orgs", []);
    }

    set(state, value) { this.state[state] = value; }
    get(state) { return this.state[state]; }

    async filterCloudsByVersion(clouds, current_cloud) {
        // fetch the current cloud's version as the baseline
        let baseline_version;
        try {
            const cacheBust = Date.now();
            const res = await fetch(`/dashboard/api/version?_=${cacheBust}`);
            const data = await res.json();
            baseline_version = String(data?.version || '');
        } catch (err) {
            Logger.warn('cloud version filter: failed to fetch baseline version, showing all clouds', err);
            return clouds;
        }
        if (!baseline_version) {
            Logger.warn('cloud version filter: baseline version is empty, showing all clouds');
            return clouds;
        }
        Logger.debug('cloud version filter: baseline version =', baseline_version);

        // probe each remote cloud's version in parallel with a timeout
        const VERSION_TIMEOUT_MS = 5000;
        const results = await Promise.allSettled(clouds.map(async cloud => {
            if (current_cloud && cloud.id === current_cloud.id) {
                return { cloud, match: true };
            }
            try {
                const controller = new AbortController();
                const timer = setTimeout(() => controller.abort(), VERSION_TIMEOUT_MS);
                const res = await fetch(`${cloud.url}/dashboard/api/version?_=${Date.now()}`, {
                    signal: controller.signal,
                });
                clearTimeout(timer);
                const data = await res.json();
                const remote_version = String(data?.version || '');
                const match = remote_version === baseline_version;
                if (!match) {
                    Logger.info(`cloud version filter: excluding "${cloud.name}" (${remote_version} != ${baseline_version})`);
                }
                return { cloud, match };
            } catch (err) {
                Logger.info(`cloud version filter: excluding "${cloud.name}" (unreachable: ${err.message})`);
                return { cloud, match: false };
            }
        }));

        const filtered = results
            .filter(r => r.status === 'fulfilled' && r.value.match)
            .map(r => r.value.cloud);

        Logger.debug(`cloud version filter: ${filtered.length}/${clouds.length} clouds available`);
        return filtered;
    }

    async reloadData() {
        //
        // fetch the provider accounts the user has access to
        //
        const userInfo = UserSession.get("userinfo");
        if (!this.initialized) {
            if (
                userInfo.realm_access?.roles?.includes('ops')
                || userInfo.realm_access?.roles?.includes('ops-admin')
                || userInfo.realm_access?.roles?.includes('developer')
                || userInfo.realm_access?.roles?.includes('developer-admin')
                || userInfo.realm_access?.roles?.includes('devops')
                || userInfo.realm_access?.roles?.includes('devops-admin')
            ) {
                // TODO: see if 'clouds' was mapped into token, if so use that array, else go fetch list
                const clouds = await Auth.fetch({ path: "/api/clouds" });
                UserSession.set("clouds", clouds);
                Logger.debug("clouds: ", clouds);
                // set the current cloud as the selected cloud based on the current host
                const current_host = new URL(window.location.href).hostname.includes('localhost') ? 'https://demo-org.homesite.cloud' : window.location.origin;
                const current_cloud = clouds.find(cloud => cloud.url === current_host);
                Logger.debug("all clouds: ", UserSession.get("clouds"));
                Logger.debug("current host: ", current_host);
                Logger.debug("found cloud: ", current_cloud);
                UserSession.set("current_cloud", current_cloud);
                UserSession.set("selected_clouds", current_cloud ? [ current_cloud ] : []);

                // filter clouds by version compatibility with the current cloud's backend
                const filtered_clouds = await this.filterCloudsByVersion(clouds, current_cloud);
                UserSession.set("filtered_clouds", filtered_clouds);
            }
            this.initialized = true;
        }

        // filter out INACTIVE providers
        const filters = '&filter=["${obj.providerstatus}","==","ACTIVE"]';

        const [ provider_list, user_provider_list ] = await Promise.all([
            Auth.fetch({ path: "/api/homesight/providers?fields=providerid,providertype,providername,displayname,orgid,orgname,orgdisplayname,createdate,admincontact,totp,territory_id" + filters }),
            Auth.fetch({ path: "/api/user/providers?fields=providerid,providername,displayname,orgid,orgname,orgdisplayname,usertype,staffadmin" }),
        ]);
        const providers = provider_list
            .filter(x => x.providertype == 'Aggregator')
            .map(x => ({ id: x.providerid, name: x.displayname || x.providername, ...x }));
        const user_providers = user_provider_list
            .map(x => ({ id: x.providerid, name: x.displayname || x.providername, ...x }));
        const unique_orgs = new Set();
        const orgs = provider_list
            .map(x => ({ id: x.orgid, name: x.orgdisplayname || x.orgname }))
            .filter(x => {
                if (unique_orgs.has(x.id)) { return false; }    // remove duplicates
                unique_orgs.add(x.id);
                return true;
            });

        Logger.debug("providers: ", providers);
        Logger.debug("user_providers: ", user_providers);
        Logger.debug("orgs: ", orgs);

        this.set("providers", providers);
        this.set("user_providers", user_providers);
        this.set("orgs", orgs);
    }
}
export const UserSession = new UserSessionSingleton();
